// Rapid ingestion modal: scan a folder, preview convertible files, queue background
// conversions (auto-process or selected), and watch job progress.
import { api, type ScanItem, type Job, type ConvertItem } from "../api";
import { toast } from "./toast";

const RECENT_KEY = "geaar.recentFolders";

function recentFolders(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]");
  } catch {
    return [];
  }
}

function rememberFolder(path: string): void {
  const list = [path, ...recentFolders().filter((p) => p !== path)].slice(0, 6);
  localStorage.setItem(RECENT_KEY, JSON.stringify(list));
}

export function openIngestPanel(
  projectId: number,
  onStarted: (jobIds: number[]) => void,
): void {
  const overlay = document.createElement("div");
  overlay.className = "modal-overlay";
  overlay.innerHTML = `
    <div class="modal">
      <div class="modal-head">
        <span>Rapid ingestion</span>
        <button class="modal-close" title="Close">×</button>
      </div>
      <div class="modal-body">
        <div class="ingest-controls">
          <input class="ingest-root" type="text" list="recent-folders"
                 placeholder="Paste a delivery folder path (e.g. P:\\...\\Received)" />
          <datalist id="recent-folders"></datalist>
          <label class="tool" title="Also scan subfolders"><input type="checkbox" class="ingest-recurse" /> Recurse</label>
          <button class="tool-btn ingest-scan">Scan</button>
          <label class="tool" title="Queue every new file immediately after scanning"><input type="checkbox" class="ingest-auto" /> Auto-process</label>
        </div>
        <div class="ingest-summary muted"></div>
        <div class="ingest-list"></div>
        <div class="ingest-actions">
          <button class="tool-btn ingest-convert" disabled>Convert selected</button>
          <button class="tool-btn ingest-all" style="display:none">Select all</button>
          <button class="tool-btn ingest-none" style="display:none">None</button>
          <span class="ingest-jobs"></span>
        </div>
      </div>
    </div>`;
  document.body.append(overlay);

  const $ = <T extends Element>(s: string) => overlay.querySelector<T>(s)!;
  const close = () => {
    if (pollTimer) window.clearInterval(pollTimer);
    overlay.remove();
  };
  $(".modal-close").addEventListener("click", close);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });

  const rootInput = $<HTMLInputElement>(".ingest-root");
  const recurse = $<HTMLInputElement>(".ingest-recurse");
  const auto = $<HTMLInputElement>(".ingest-auto");
  const summary = $<HTMLDivElement>(".ingest-summary");
  const list = $<HTMLDivElement>(".ingest-list");
  const convertBtn = $<HTMLButtonElement>(".ingest-convert");
  const allBtn = $<HTMLButtonElement>(".ingest-all");
  const noneBtn = $<HTMLButtonElement>(".ingest-none");
  const jobsEl = $<HTMLSpanElement>(".ingest-jobs");

  // Prefill from history so re-scanning a delivery is one click.
  const recents = recentFolders();
  const datalist = $<HTMLDataListElement>("#recent-folders");
  for (const p of recents) {
    const opt = document.createElement("option");
    opt.value = p;
    datalist.append(opt);
  }
  if (recents[0]) rootInput.value = recents[0];

  allBtn.addEventListener("click", () => {
    for (const it of items) {
      const cb = checks.get(it.path);
      if (cb && it.convertible && !it.cached) cb.checked = true;
    }
    refreshConvertBtn();
  });
  noneBtn.addEventListener("click", () => {
    for (const cb of checks.values()) cb.checked = false;
    refreshConvertBtn();
  });

  let items: ScanItem[] = [];
  const checks = new Map<string, HTMLInputElement>();
  let pollTimer: number | null = null;

  function selected(): ScanItem[] {
    return items.filter((it) => checks.get(it.path)?.checked && it.convertible);
  }
  function refreshConvertBtn() {
    convertBtn.disabled = selected().length === 0;
  }

  const scanBtn = $<HTMLButtonElement>(".ingest-scan");
  scanBtn.addEventListener("click", async () => {
    const root = rootInput.value.trim();
    if (!root) {
      rootInput.focus();
      return;
    }
    summary.textContent = "Scanning…";
    scanBtn.disabled = true;
    list.innerHTML = "";
    try {
      const res = await api.scanFolder(projectId, root, recurse.checked);
      rememberFolder(root);
      items = res.items;
      const cached = items.filter((i) => i.cached).length;
      summary.textContent =
        `${res.count} convertible found · ${res.matched} match the register` +
        (cached ? ` · ${cached} already converted` : "");
      renderList();
      allBtn.style.display = noneBtn.style.display = items.length ? "" : "none";
      if (auto.checked) {
        items.forEach((it) => {
          const c = checks.get(it.path);
          if (c && it.convertible && !it.cached) c.checked = true;
        });
        refreshConvertBtn();
        void doConvert();
      }
    } catch (err) {
      summary.textContent = `Scan failed: ${(err as Error).message}`;
    } finally {
      scanBtn.disabled = false;
    }
  });
  rootInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") scanBtn.click();
  });

  function renderList() {
    list.innerHTML = "";
    checks.clear();
    for (const it of items) {
      const row = document.createElement("label");
      row.className = "ingest-row" + (it.convertible ? "" : " disabled");
      const cb = document.createElement("input");
      cb.type = "checkbox";
      cb.disabled = !it.convertible;
      cb.addEventListener("change", refreshConvertBtn);
      checks.set(it.path, cb);
      const dim = `<span class="badge ${it.dimension === "2D" ? "chip" : "muted"}">${it.dimension}</span>`;
      const tags: string[] = [];
      if (it.cached) tags.push('<span class="badge ok">cached</span>');
      if (it.ext === ".fbx" && !it.has_offset) tags.push('<span class="badge warn">no offset</span>');
      const match = it.match_item_name
        ? `<span class="ingest-match">→ ${it.match_item_name}</span>`
        : '<span class="ingest-match unlinked">unlinked</span>';
      row.append(cb);
      const info = document.createElement("span");
      info.className = "ingest-info";
      info.innerHTML = `${dim} <span class="ingest-name">${it.name}${it.ext}</span>
        <span class="muted">${it.size_mb} MB</span> ${tags.join(" ")} ${match}`;
      row.append(info);
      list.append(row);
    }
    refreshConvertBtn();
  }

  async function doConvert() {
    const sel = selected();
    if (!sel.length) return;
    const payload: ConvertItem[] = sel.map((it) => ({
      path: it.path,
      source_epsg: it.source_epsg,
      offset: it.offset,
      register_item_id: it.match_item_id,
    }));
    convertBtn.disabled = true;
    try {
      const { jobs } = await api.convertItems(projectId, payload);
      onStarted(jobs.map((j) => j.id)); // global progress widget + tree refresh
      toast(`Queued ${jobs.length} conversion${jobs.length > 1 ? "s" : ""} - progress in the top bar`, "ok");
      startPolling();
    } catch (err) {
      jobsEl.textContent = `Convert failed: ${(err as Error).message}`;
    }
  }
  convertBtn.addEventListener("click", () => void doConvert());

  function startPolling() {
    // Modal-local detail chips; the global activity bar + tree refresh are handled by
    // the watcher started via onStarted().
    if (pollTimer) window.clearInterval(pollTimer);
    pollTimer = window.setInterval(async () => {
      const { jobs } = await api.listJobs(projectId);
      const recent = jobs.slice(0, 12);
      const active = recent.filter((j) => j.status === "queued" || j.status === "running");
      jobsEl.innerHTML = recent.map(jobChip).join(" ");
      if (active.length === 0) {
        if (pollTimer) window.clearInterval(pollTimer);
        pollTimer = null;
      }
    }, 1500);
  }

  function jobChip(j: Job): string {
    const cls = j.status === "succeeded" ? "ok" : j.status === "failed" ? "warn" : "chip";
    const pct = j.status === "running" ? ` ${Math.round(j.progress)}%` : "";
    return `<span class="badge ${cls}" title="${j.message ?? ""}">${j.label ?? j.id}: ${j.status}${pct}</span>`;
  }
}
